/**
 * Panel layout, chat history sidebar and upload helpers of the HaulierMagic assistant (CU-14zefhy38pt W2; twin of the RouteMagic
 * dock and history, CU-14zefhy2ydj). Pure helpers (no Angular), so they run under node in the spec.
 */

// ----- U1 / U2 / B8: where the panel sits and how wide it is -----------------------------

/** Base name of the localStorage key of the panel layout (kept per person like the chats). */
export const LAYOUT_KEY_BASE = 'hm-assistant-state-v2-layout';
/** The docked panel is 30-70% of the window wide (drag the left edge). */
export const DOCK_MIN_PCT = 30;
export const DOCK_MAX_PCT = 70;
export const DOCK_DEFAULT_PCT = 40;
/** B8: below this window width the panel fills the screen (no offset, no cut-off controls). */
export const FULLSCREEN_BELOW_PX = 600;
/** The history list is open beside the chat by default from this panel width. */
export const SIDEBAR_BESIDE_PX = 760;
/** Windows narrower than this open the floating panel by default (a docked 40% would be too thin). */
export const DOCK_DEFAULT_FROM_PX = 1000;

export interface PanelLayout {
    /** 'dock' = a side panel on the right edge that leaves the page usable; 'float' = the old popover */
    mode: 'dock' | 'float';
    /** docked width as a share of the window */
    widthPct: number;
    /** the panel fills the window (the full-screen toggle) */
    full: boolean;
    /** the chat history sidebar is open */
    sidebar: boolean;
}

/** A docked width inside 30-70%; the default for anything that is not a number. */
export function clampWidthPct(value: any): number {
    const n = typeof value === 'string' && value.trim() ? Number(value) : value;
    if (typeof n !== 'number' || !isFinite(n)) {
        return DOCK_DEFAULT_PCT;
    }
    return Math.min(DOCK_MAX_PCT, Math.max(DOCK_MIN_PCT, Math.round(n * 10) / 10));
}

export function defaultLayout(viewportWidth: number): PanelLayout {
    return {
        mode: viewportWidth >= DOCK_DEFAULT_FROM_PX ? 'dock' : 'float',
        widthPct: DOCK_DEFAULT_PCT,
        full: false,
        // the history list sits beside the chat only when the docked panel is wide enough; else it opens as a drawer on demand
        sidebar: (viewportWidth * DOCK_DEFAULT_PCT) / 100 >= SIDEBAR_BESIDE_PX
    };
}

/** The stored layout (JSON text) completed with defaults; anything unreadable gives the default. */
export function readLayout(raw: string | null | undefined, viewportWidth: number): PanelLayout {
    const base = defaultLayout(viewportWidth);
    if (!raw) {
        return base;
    }
    try {
        const stored = JSON.parse(raw);
        return {
            mode: stored?.mode === 'float' || stored?.mode === 'dock' ? stored.mode : base.mode,
            widthPct: clampWidthPct(stored?.widthPct ?? base.widthPct),
            full: stored?.full === true,
            sidebar: typeof stored?.sidebar === 'boolean' ? stored.sidebar : base.sidebar
        };
    } catch (e) {
        return base;
    }
}

/** The docked width (%) that puts the panel's left edge under the pointer; the panel is anchored to the right edge. */
export function widthPctFromPointer(clientX: number, viewportWidth: number): number {
    if (!viewportWidth || viewportWidth <= 0) {
        return DOCK_DEFAULT_PCT;
    }
    return clampWidthPct(((viewportWidth - clientX) / viewportWidth) * 100);
}

/** B8: the panel is full screen on a phone-width window, whatever the stored layout says. */
export function isFullscreenWidth(viewportWidth: number): boolean {
    return viewportWidth > 0 && viewportWidth < FULLSCREEN_BELOW_PX;
}

// ----- U4 / B7 / B11: the chat history sidebar -------------------------------------------

/** A label the panel made itself ("Session 3"): replaced by the first question once there is one. */
export function isAutoLabel(label: string | null | undefined): boolean {
    return !label || /^Session \d+$/.test(label.trim());
}

/** The title of a chat from its first question: one line, at most 60 characters. */
export function titleFromQuestion(text: string | null | undefined): string {
    const line = (text || '').replace(/\s+/g, ' ').trim();
    return line.length > 60 ? line.substring(0, 59).trimEnd() + '…' : line;
}

/** The longest label the server stores for a chat (J-2). */
export const LABEL_MAX = 100;

/** A renamed label: trimmed, one line, at most 100 characters; '' = keep the old one. */
export function cleanLabel(text: string | null | undefined): string {
    return (text || '').replace(/\s+/g, ' ').trim().substring(0, LABEL_MAX);
}

export type HistoryGroup = 'Pinned' | 'Today' | 'Yesterday' | 'Previous 7 days' | 'Older';

/** The date group of a chat (calendar days of the local clock). */
export function historyGroup(updatedAt: number, now: number): Exclude<HistoryGroup, 'Pinned'> {
    const startOf = (t: number) => {
        const d = new Date(t);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    };
    const days = Math.round((startOf(now) - startOf(updatedAt || 0)) / 86400000);
    if (days <= 0) {
        return 'Today';
    }
    if (days === 1) {
        return 'Yesterday';
    }
    return days <= 7 ? 'Previous 7 days' : 'Older';
}

/** What the sidebar knows of one chat (a tab of this browser, or an archived chat that only the server has). */
export interface HistoryItem {
    key: string;
    label: string;
    updatedAt: number;
    pinned: boolean;
    archived: boolean;
    /** the lower-cased question and answer text, for the local search */
    text?: string;
}

export interface HistoryRow extends HistoryItem {
    group: HistoryGroup;
    /** first row of its group: the sidebar prints the group name above it */
    groupStart: boolean;
}

/**
 * The sidebar rows: pinned chats first, then newest first, grouped by date. Archived chats show only with `showArchived` -
 * and are always found by a search. The search matches the title and (for chats in this browser) the text.
 */
export function historyRows(items: HistoryItem[], query: string, showArchived: boolean, now: number, keepKey = ''): HistoryRow[] {
    const q = (query || '').trim().toLowerCase();
    const shown = (items || []).filter((i) => {
        if (i.archived && !showArchived && !q && i.key !== keepKey) {
            return false;
        }
        return !q || (i.label || '').toLowerCase().includes(q) || (i.text || '').includes(q);
    });
    shown.sort((a, b) => Number(b.pinned) - Number(a.pinned) || (b.updatedAt || 0) - (a.updatedAt || 0));
    let previous = '';
    return shown.map((i) => {
        const group: HistoryGroup = i.pinned ? 'Pinned' : historyGroup(i.updatedAt, now);
        const row: HistoryRow = {...i, group, groupStart: group !== previous};
        previous = group;
        return row;
    });
}

/** The server list is read again on open when the last read is older than this (B7/B11: tabs follow the server). */
export const HISTORY_REFRESH_MS = 30000;

export function historyDue(lastReadAt: number, now: number): boolean {
    return !lastReadAt || now - lastReadAt >= HISTORY_REFRESH_MS;
}

// ----- U9: "show work" while a turn runs -----------------------------------------------------

export const WORK_STEPS_MAX = 30;

/** The steps a turn went through: a label that repeats the last one is not added again; the newest 30 are kept. */
export function addWorkStep(steps: string[] | undefined, label: string | null | undefined): string[] {
    const list = steps || [];
    const text = (label || '').trim();
    if (!text || list[list.length - 1] === text) {
        return list;
    }
    return [...list, text].slice(-WORK_STEPS_MAX);
}

// ----- U1 / U3 / C8 helpers ----------------------------------------------------------------

/** Below this panel width the history sidebar covers the chat (a drawer) instead of sitting beside it. */
export const NARROW_PANEL_PX = 640;
/** The floating panel is 70% of the window, at least 380 px (as before). */
const FLOAT_MIN_PX = 380;

/** The panel's width in pixels for a layout and window width. */
export function panelWidthPx(layout: PanelLayout, viewportWidth: number): number {
    if (layout.full || isFullscreenWidth(viewportWidth)) {
        return viewportWidth;
    }
    return layout.mode === 'dock'
        ? (viewportWidth * layout.widthPct) / 100
        : Math.min(viewportWidth * 0.92, Math.max(FLOAT_MIN_PX, viewportWidth * 0.7));
}

/** U3: the question box grows with its text up to 40% of the panel height. */
export function growHeight(scrollHeight: number, panelHeight: number): {height: number; scroll: boolean} {
    const max = Math.max(60, Math.floor((panelHeight || 0) * 0.4) || 240);
    return {height: Math.min(scrollHeight, max), scroll: scrollHeight > max};
}

/** C8: at most this many files go up in one drop or paste. */
export const UPLOAD_AT_ONCE_MAX = 10;

/** C8: a name for a pasted image/file (browsers call every pasted image "image.png"). */
export function pastedFileName(original: string | null | undefined, mimeType: string | null | undefined, index: number, now: number): string {
    const name = (original || '').trim();
    if (name && !/^image\.[a-z0-9]+$/i.test(name)) {
        return name;
    }
    const ext = ((mimeType || '').split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '').toLowerCase() || 'png';
    return `pasted-${now}-${index + 1}.${ext}`;
}

/** C8: a drag carries files (not just text or a link). */
export function dragHasFiles(types: ArrayLike<string> | null | undefined): boolean {
    return !!types && Array.prototype.indexOf.call(types, 'Files') >= 0;
}
